import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ThinkingTrace.tsx -- collapsible <details> panel above each assistant
// message showing the agent's tool-call trail.
//
// Per design \u00a76.3 + the user's explicit ask: "I like the thinking sections
// showing what it's doing, but those should be able to be expanded or
// contracted." Default state is collapsed once the assistant message is
// complete; while still streaming, default is expanded so the user can see
// progress. Per-trace state is local; toggling one doesn't toggle others.
//
// Each entry shows:
//   - status glyph (in-flight spinner / success check / error x)
//   - tool name in monospace
//   - args summary (one-line, truncated)
//   - duration (when complete)
//   - on hover: full args + result preview in a tooltip-style block
import { useState } from 'react';
export function ThinkingTrace({ thinking, streaming }) {
    const [open, setOpen] = useState(streaming);
    if (thinking.length === 0)
        return null;
    // Compute summary for collapsed view: counts by tool name + total duration.
    const counts = new Map();
    let totalMs = 0;
    let errors = 0;
    for (const t of thinking) {
        counts.set(t.toolName, (counts.get(t.toolName) ?? 0) + 1);
        if (t.durationMs)
            totalMs += t.durationMs;
        if (t.state === 'error')
            errors += 1;
    }
    const summaryParts = [];
    for (const [name, n] of counts)
        summaryParts.push(`${n} ${name}`);
    const durStr = totalMs > 0 ? ` \u00b7 ${(totalMs / 1000).toFixed(1)}s` : '';
    const errStr = errors > 0 ? ` \u00b7 ${errors} error${errors === 1 ? '' : 's'}` : '';
    return (_jsxs("details", { className: `thinking-trace ${streaming ? 'streaming' : 'complete'}`, open: open, onToggle: (e) => setOpen(e.target.open), children: [_jsxs("summary", { children: [_jsx("span", { className: "thinking-glyph", "aria-hidden": true, children: streaming ? '\u23F3' : '\uD83D\uDCAD' }), _jsx("span", { className: "thinking-summary-text", children: streaming ? 'Thinking\u2026' : 'Thought process' }), _jsxs("span", { className: "thinking-summary-meta", children: [summaryParts.join(', '), durStr, errStr] })] }), _jsx("ol", { className: "thinking-list", children: thinking.map((t, i) => (_jsxs("li", { className: `thinking-item ${t.state}`, children: [_jsx("span", { className: "thinking-item-status", "aria-hidden": true, children: stateGlyph(t.state) }), _jsx("span", { className: "thinking-item-name", children: t.toolName }), _jsx("span", { className: "thinking-item-args", title: JSON.stringify(t.args, null, 2), children: summarizeArgs(t.args) }), t.durationMs !== undefined && (_jsxs("span", { className: "thinking-item-duration", children: [(t.durationMs / 1000).toFixed(1), "s"] })), t.state === 'error' && t.error && (_jsx("span", { className: "thinking-item-error", title: t.error, children: t.error })), t.preview && (_jsx("span", { className: "thinking-item-preview", title: t.preview, children: truncate(t.preview, 100) }))] }, i))) })] }));
}
function stateGlyph(s) {
    switch (s) {
        case 'in-flight': return '\u23F3';
        case 'ok': return '\u2713';
        case 'error': return '\u2717';
    }
}
function summarizeArgs(args) {
    const keys = Object.keys(args);
    if (keys.length === 0)
        return '\u2014';
    // Render up to 2 key/value pairs.
    const parts = [];
    for (const k of keys.slice(0, 2)) {
        const v = args[k];
        let s;
        if (typeof v === 'string')
            s = `"${truncate(v, 40)}"`;
        else if (v === null || v === undefined)
            s = 'null';
        else if (typeof v === 'object')
            s = '{\u2026}';
        else
            s = String(v);
        parts.push(`${k}=${s}`);
    }
    if (keys.length > 2)
        parts.push(`+${keys.length - 2}`);
    return parts.join(', ');
}
function truncate(s, n) {
    if (s.length <= n)
        return s;
    return s.slice(0, n - 1) + '\u2026';
}
